export interface AdminUser {
  merchantId: string;
  email: string;
  role: 'admin' | 'viewer';
  isSuperAdmin?: boolean;
  isAccountOwner?: boolean;
  teamRole?: 'admin' | 'developer' | 'viewer' | null;
  teamMemberId?: string | null;
}

// Public API base for production fallback when no env override is provided
// and the app is hosted on a different domain (e.g., Vercel) without a
// reverse proxy for /admin.
const DEFAULT_PUBLIC_API = 'https://api.walletgate.app';

function resolveBaseUrl(): string {
  const meta = (import.meta as unknown as { env?: { VITE_ADMIN_API_BASE_URL?: string; VITE_API_BASE_URL?: string } }).env;

  // Debug logging in development
  if (import.meta.env?.DEV) {
    console.warn('[API] Resolving base URL:', {
      VITE_ADMIN_API_BASE_URL: meta?.VITE_ADMIN_API_BASE_URL,
      VITE_API_BASE_URL: meta?.VITE_API_BASE_URL,
      hostname: typeof window !== 'undefined' ? window.location.hostname : 'N/A',
    });
  }

  // Preferred explicit admin base URL (full path to /admin root)
  if (meta?.VITE_ADMIN_API_BASE_URL) {
    const configured = meta.VITE_ADMIN_API_BASE_URL;
    // In development, force relative '/admin' to ensure same-origin cookie flow via Vite proxy
    if (import.meta.env?.DEV) {
      const abs = /^(http|https):\/\//i.test(configured);
      if (abs) {
        console.warn('[API] Overriding absolute VITE_ADMIN_API_BASE_URL in dev to use \'/admin\' via Vite proxy to avoid cross-origin cookie issues. Configured:', configured);
        return '/admin';
      }
    }
    if (import.meta.env?.DEV) console.warn('[API] Using VITE_ADMIN_API_BASE_URL:', configured);
    return configured;
  }

  // Backward-compat: if VITE_API_BASE_URL is provided, append /admin
  if (meta?.VITE_API_BASE_URL) {
    const base = meta.VITE_API_BASE_URL.replace(/\/+$/, '');
    const url = `${base}/admin`;
    if (import.meta.env?.DEV) console.warn('[API] Using VITE_API_BASE_URL + /admin:', url);
    return url;
  }

  // Development fallback: Use relative URL to go through Vite proxy
  if (import.meta.env?.DEV) {
    if (import.meta.env?.DEV) console.warn('[API] Using dev fallback: /admin');
    return '/admin';
  }

  // Production fallback: detect from window location
  if (typeof window !== 'undefined') {
    const h = window.location.hostname;
    const protocol = window.location.protocol || 'http:';
    if (h === 'localhost' || h === '127.0.0.1' || h.endsWith('.localhost')) {
      return `${protocol}//${h}:4000/admin`;
    }
    // Hosted on a different site (e.g., Vercel) without an /admin proxy.
    // Fall back to the public API domain so full-page redirects (OAuth)
    // and fetch() calls go directly to the API instead of hitting the SPA.
    return `${DEFAULT_PUBLIC_API}/admin`;
  }

  return '/admin';
}

const BASE_URL = resolveBaseUrl();
const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;
const PLAYGROUND_LONG_RUNNING_TIMEOUT_MS = 60_000;
const LOGIN_ROUTE_PATH = '/login';
const AUTH_REDIRECT_EXEMPT_PREFIXES = [
  '/auth/login',
  '/auth/register',
  '/auth/status',
  '/auth/reset-password/request',
  '/auth/reset-password/confirm',
  '/health/status',
];

// Expose the resolved admin API base for consumers that need to perform
// full-page redirects (e.g., OAuth initiation) instead of fetch() calls.
export function getAdminApiBase(): string {
  return BASE_URL;
}
let csrfToken: string | null = null;

export function setCsrfToken(token: string): void {
  if (!token) {
    csrfToken = null;
    try {
      sessionStorage.removeItem('wg_csrf');
    } catch {
      // Ignore sessionStorage errors (e.g., in incognito mode)
    }
    return;
  }

  csrfToken = token;
  try {
    sessionStorage.setItem('wg_csrf', token);
  } catch {
    // Ignore sessionStorage errors (e.g., in incognito mode)
  }
}

export function getCsrfToken(): string | null {
  if (csrfToken) return csrfToken;
  try {
    return sessionStorage.getItem('wg_csrf');
  } catch {
    return null;
  }
}

interface RequestBehavior {
  timeoutMs?: number;
}

function isAuthRedirectExempt(path: string): boolean {
  return AUTH_REDIRECT_EXEMPT_PREFIXES.some((prefix) =>
    path === prefix || path.startsWith(`${prefix}?`) || path.startsWith(`${prefix}/`),
  );
}

function redirectToLoginOnUnauthorized(): void {
  if (typeof window === 'undefined') return;
  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (window.location.pathname === LOGIN_ROUTE_PATH) return;
  const redirectTarget = currentPath || '/admin';
  const destination = `${LOGIN_ROUTE_PATH}?redirect=${encodeURIComponent(redirectTarget)}`;
  window.location.replace(destination);
}

async function request<T>(path: string, options: RequestInit = {}, behavior: RequestBehavior = {}): Promise<T> {
  const headers = new Headers(options.headers || {});
  if (!headers.has('Content-Type') && options.method && options.method !== 'GET') {
    headers.set('Content-Type', 'application/json');
  }
  if (options.method && options.method !== 'GET') {
    const token = getCsrfToken();
    if (token) headers.set('x-csrf-token', token);
  }

  const method = (options.method || 'GET').toUpperCase();
  const isGet = method === 'GET';
  const maxAttempts = isGet ? 2 : 1;
  const retryableStatuses = new Set([502, 503, 504, 525]);
  const timeoutMs = behavior.timeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;
  const timeoutSeconds = Math.round(timeoutMs / 1000);

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const res = await fetch(`${BASE_URL}${path}`, {
        ...options,
        credentials: 'include',
        headers,
        signal: controller.signal,
      });

      if (!res.ok) {
        if (isGet && retryableStatuses.has(res.status) && attempt < maxAttempts) {
          clearTimeout(timeoutId);
          await new Promise((resolve) => setTimeout(resolve, 400 * attempt));
          continue;
        }
        const err = (await safeJson(res)) as { error?: string; message?: string } | null;
        if (res.status === 401 && !isAuthRedirectExempt(path)) {
          // Session likely expired while user is active: clear stale client auth and redirect immediately.
          setCsrfToken('');
          redirectToLoginOnUnauthorized();
        }
        throw new Error(err?.error || err?.message || `Request failed (${res.status})`);
      }

      const data = (await safeJson(res)) as T;
      clearTimeout(timeoutId);
      return data;
    } catch (error) {
      clearTimeout(timeoutId);
      if ((error as Error).name === 'AbortError') {
        if (isGet && attempt < maxAttempts) {
          await new Promise((resolve) => setTimeout(resolve, 400 * attempt));
          continue;
        }
        throw new Error(`Request timed out after ${timeoutSeconds}s. Please retry.`);
      }
      if (isGet && attempt < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 400 * attempt));
        continue;
      }
      throw error;
    }
  }

  throw new Error('Request failed after retries');
}

async function safeJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

export const api = {
  // Generic HTTP methods for flexibility
  async get<T = any>(path: string): Promise<{ data: T }> {
    const data = await request<T>(path, { method: 'GET' });
    return { data };
  },
  // Owner APIs
  async ownerMe(): Promise<{ success: boolean; isOwner: boolean }> {
    return await request('/owner/me', { method: 'GET' });
  },
  async ownerAuditRecent(limit = 3): Promise<{
    data: Array<{ id: string; action: string; timestamp: string; details?: any; severity?: string }>;
  }> {
    // Delegate to generic GET so tests can spy on api.get while keeping runtime behavior identical
    return await api.get(`/owner/audit/recent?limit=${limit}`);
  },
  async ownerSetSuperAdmin(
    email: string,
    action: 'grant' | 'revoke',
    reason: string,
  ): Promise<{ success: boolean; data: { id: string; email: string; isSuperAdmin: boolean } }> {
    return await request('/owner/super-admin', {
      method: 'POST',
      body: JSON.stringify({ email, action, reason }),
    });
  },
  async post<T = any>(path: string, body?: any): Promise<{ data: T }> {
    const data = await request<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
    return { data };
  },
  async patch<T = any>(path: string, body?: any): Promise<{ data: T }> {
    const data = await request<T>(path, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
    return { data };
  },
  async delete<T = any>(path: string): Promise<{ data: T }> {
    const data = await request<T>(path, { method: 'DELETE' });
    return { data };
  },

  async status(): Promise<{ loggedIn: boolean; csrfToken?: string; admin?: AdminUser }> {
    const raw = (await request('/auth/status', { method: 'GET' })) as any;
    if (!raw?.loggedIn) return { loggedIn: false };
    const admin: AdminUser = {
      merchantId: raw.user?.id,
      email: raw.user?.email,
      role: 'admin',
      isSuperAdmin: raw.user?.isSuperAdmin || false,
      isAccountOwner: raw.user?.isAccountOwner !== false,
      teamRole: raw.user?.teamRole || null,
      teamMemberId: raw.user?.teamMemberId || null,
    };
    if (raw?.csrfToken) setCsrfToken(raw.csrfToken);
    return { loggedIn: true, csrfToken: raw?.csrfToken, admin };
  },
  async register(input: {
    email: string;
    password: string;
    name: string;
    companyName?: string;
    inviteToken?: string;
    recaptchaToken?: string;
  }): Promise<{ success: boolean; csrfToken: string; apiKey: string; teamAccepted?: boolean }> {
    const data = await request<{ success: boolean; csrfToken: string; apiKey: string; teamAccepted?: boolean }>(
      '/auth/register',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
    );
    if (data?.csrfToken) setCsrfToken(data.csrfToken);
    return data as any;
  },
  async login(email: string, password: string): Promise<{ csrfToken: string }> {
    const data = await request<{ success: boolean; csrfToken: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    setCsrfToken(data.csrfToken);
    return { csrfToken: data.csrfToken };
  },
  async logout(): Promise<void> {
    await request('/auth/logout', { method: 'POST' });
    setCsrfToken('');
  },
  async me(): Promise<{ admin: AdminUser; csrfToken?: string }> {
    const raw = (await request('/auth/me', { method: 'GET' })) as any;
    const admin: AdminUser = {
      merchantId: raw?.user?.id,
      email: raw?.user?.email,
      role: 'admin',
      isSuperAdmin: raw?.user?.isSuperAdmin || false,
      isAccountOwner: raw?.user?.isAccountOwner !== false,
      teamRole: raw?.user?.teamRole || null,
      teamMemberId: raw?.user?.teamMemberId || null,
    };
    return { admin, csrfToken: raw?.csrfToken };
  },
  async listKeys(params?: {
    page?: number;
    limit?: number;
  }): Promise<{ success: boolean; data: unknown[]; meta?: unknown; showOnceApiKey?: string }> {
    const qs = new URLSearchParams();
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return await request(`/keys${suffix}`, { method: 'GET' });
  },
  async createKey(
    environment: 'test' | 'live',
    name?: string,
    expiresDays?: number,
    expiresAt?: string,
  ): Promise<{ success: boolean; data: unknown; apiKey: string }> {
    const resolvedExpiresAt =
      expiresAt ||
      (typeof expiresDays === 'number' && expiresDays > 0
        ? new Date(Date.now() + expiresDays * 24 * 60 * 60 * 1000).toISOString()
        : undefined);
    return await request('/keys', {
      method: 'POST',
      body: JSON.stringify({ environment, name, expiresAt: resolvedExpiresAt }),
    });
  },
  async rotateKey(id: string): Promise<{ success: boolean; data: unknown; apiKey: string }> {
    return await request(`/keys/${id}/rotate`, { method: 'POST' });
  },
  async revokeKey(id: string): Promise<{ success: boolean; data: unknown }> {
    return await request(`/keys/${id}/revoke`, { method: 'POST' });
  },
  async getRedirectAllowlist(): Promise<{
    success: boolean;
    data: { redirectAllowlist: string[]; enforce: boolean };
  }> {
    return await request('/redirect-allowlist', { method: 'GET' });
  },
  async updateRedirectAllowlist(redirectAllowlist: string[]): Promise<{
    success: boolean;
    data: { redirectAllowlist: string[] };
  }> {
    return await request('/redirect-allowlist', {
      method: 'PUT',
      body: JSON.stringify({ redirectAllowlist }),
    });
  },
  async listWebhooks(params?: {
    page?: number;
    limit?: number;
  }): Promise<{ success: boolean; data: unknown[]; meta?: unknown }> {
    const qs = new URLSearchParams();
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return await request(`/webhooks${suffix}`, { method: 'GET' });
  },
  async createWebhook(
    url: string,
    events: string[],
  ): Promise<{ success: boolean; data: unknown; secret: string }> {
    return await request('/webhooks', { method: 'POST', body: JSON.stringify({ url, events }) });
  },
  async deleteWebhook(id: string): Promise<{ success: boolean; message?: string }> {
    return await request(`/webhooks/${id}`, { method: 'DELETE' });
  },
  async getWebhookSecret(id: string): Promise<{ success: boolean; secret: string }> {
    return await request(`/webhooks/${id}/secret`, { method: 'GET' });
  },
  async rotateWebhookSecret(
    id: string,
  ): Promise<{ success: boolean; data: { webhook: unknown; secret: string } }> {
    return await request(`/webhooks/${id}/rotate-secret`, { method: 'POST' });
  },
  async getWebhookDeliveries(id: string): Promise<{ success: boolean; data: unknown[] }> {
    return await request(`/webhooks/${id}/deliveries`, { method: 'GET' });
  },
  async webhookDeliveries(params?: {
    limit?: number;
    offset?: number;
  }): Promise<{
    success: boolean;
    data: unknown[];
    pagination?: { total: number; limit: number; offset: number; hasMore: boolean };
  }> {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return await request(`/webhooks/deliveries${suffix}`, { method: 'GET' });
  },
  async webhookDeliveryDetail(id: string): Promise<{ success: boolean; data: unknown }> {
    return await request(`/webhooks/deliveries/${id}`, { method: 'GET' });
  },
  async retryWebhookDelivery(
    webhookId: string,
    deliveryId: string,
  ): Promise<{ success: boolean; message?: string }> {
    return await request(`/webhooks/${webhookId}/deliveries/${deliveryId}/retry`, {
      method: 'POST',
    });
  },
  async onboardingProgress(): Promise<{
    success: boolean;
    data: {
      steps: Record<string, boolean>;
      coreSteps: string[];
      completedCount: number;
      totalSteps: number;
      completionPercentage: number;
      isComplete: boolean;
      tier: string;
    };
  }> {
    return await request('/onboarding/progress', { method: 'GET' });
  },

  async metrics(): Promise<{
    success: boolean;
    data: {
      total: number;
      completed: number;
      failed: number;
      pending: number;
      activeSessions: number;
      successRate: number;
    };
  }> {
    return await request('/metrics', { method: 'GET' });
  },
  async metricsTimeseries(
    rangeDays = 7,
    bucket: 'day' | 'hour' = 'day',
  ): Promise<{
    success: boolean;
    data: Array<{ time: string; completed: number; failed: number }>;
  }> {
    const params = new URLSearchParams({ rangeDays: String(rangeDays), bucket });
    return await request(`/metrics/timeseries?${params.toString()}`, { method: 'GET' });
  },
  async billing(): Promise<{
    success: boolean;
    data: {
      plan: {
        name: string;
        tier: string;
        price: number;
        limits: { monthlyVerifications: number };
        analytics: string;
        features?: any;
      };
      analytics?: {
        currentMonth?: { total: number; period?: { start: string; end: string } };
        breakdown?: {
          byEnvironment: { live: number; test: number };
          byStatus: { completed: number; failed: number; successRate: number };
        };
        timeseries?: Array<{ date: string; completed: number; failed: number }>;
        topApiKeys?: Array<{ publicId: string; name: string; environment: string; count: number }>;
      };
      currentMonth: { total: number; period?: { start: string; end: string } };
      subscription?: {
        currentPeriodEnd: string;
        status: string;
      };
      gracePeriod?: {
        isInGracePeriod: boolean;
        daysRemaining: number;
      };
    };
  }> {
    return await request('/billing', { method: 'GET' });
  },
  // billingPortal already declared above
  async billingPortal(): Promise<{ success: boolean; url: string }> {
    return await request('/billing/portal', { method: 'POST' });
  },
  async billingCheckout(plan: 'pro' | 'business'): Promise<{ url?: string; upgraded?: boolean; message?: string }> {
    const res = (await request('/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({ plan }),
    })) as any;
    if (res.upgraded) return { upgraded: true, message: res.message };
    return { url: res.url };
  },
  async audit(params?: {
    category?: string;
    severity?: string;
    page?: number;
    limit?: number;
  }): Promise<{ success: boolean; data: unknown[]; meta?: unknown }> {
    const qs = new URLSearchParams();
    if (params?.category) qs.set('category', params.category);
    if (params?.severity) qs.set('severity', params.severity);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return await request(`/audit${suffix}`, { method: 'GET' });
  },
  async listSessions(params?: {
    q?: string;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<{ success: boolean; data: unknown[]; meta?: unknown }> {
    const qs = new URLSearchParams();
    if (params?.q) qs.set('q', params.q);
    if (params?.status) qs.set('status', params.status);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return await request(`/sessions${suffix}`, { method: 'GET' });
  },
  async getSession(id: string): Promise<{ success: boolean; data: unknown }> {
    return await request(`/sessions/${id}`, { method: 'GET' });
  },
  async getSettings(): Promise<{
    success: boolean;
    data: { retentionDays: number; privacyMode: boolean };
  }> {
    return await request('/settings', { method: 'GET' });
  },
  async saveSettings(input: {
    retentionDays: number;
    privacyMode: boolean;
  }): Promise<{ success: boolean }> {
    return await request('/settings', { method: 'POST', body: JSON.stringify(input) });
  },
  async superMe(): Promise<{ success: boolean; isSuperAdmin: boolean }> {
    return await request('/super/me', { method: 'GET' });
  },
  async superListMerchants(params?: {
    q?: string;
    status?: string;
    tier?: string;
    page?: number;
    limit?: number;
  }): Promise<{ success: boolean; data: unknown[]; meta: unknown }> {
    const qs = new URLSearchParams();
    if (params?.q) qs.set('q', params.q);
    if (params?.status) qs.set('status', params.status);
    if (params?.tier) qs.set('tier', params.tier);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return await request(`/super/merchants${suffix}`, { method: 'GET' });
  },
  async superGetMerchant(id: string): Promise<{ success: boolean; data: unknown }> {
    return await request(`/super/merchants/${id}`, { method: 'GET' });
  },
  async superSetTier(id: string, tier: string): Promise<{ success: boolean }> {
    return await request(`/super/merchants/${id}/tier`, {
      method: 'POST',
      body: JSON.stringify({ tier }),
    });
  },
  async superSetStatus(id: string, status: string): Promise<{ success: boolean }> {
    return await request(`/super/merchants/${id}/status`, {
      method: 'POST',
      body: JSON.stringify({ status }),
    });
  },
  // Super Admin APIs
  async listSuperAdminUsers(page = 1): Promise<{
    success: boolean;
    data: Array<{
      id: string;
      email: string;
      name: string;
      companyName?: string;
      securityTier: string;
      status: string;
      isEmailVerified: boolean;
      isSuperAdmin: boolean;
      authProvider?: string;
      createdAt: string;
      updatedAt: string;
      usage: { test: number; live: number; limits: { test: number; live: number } };
      _count: { apiKeys: number; sessions: number; webhooks: number };
    }>;
    pagination?: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  }> {
    return await request(`/superadmin/users?page=${page}`, { method: 'GET' });
  },
  async getSuperAdminUser(id: string): Promise<{
    success: boolean;
    data: {
      merchant: any;
      statistics: {
        sessions: { total: number; completed: number; failed: number; thisMonth: number };
        apiKeys: { total: number; active: number };
      };
      plan: {
        tier: string;
        name: string;
        price: number;
        limits: {
          testVerifications: number;
          liveVerifications: number;
          maxTestKeys: number;
          maxLiveKeys: number;
        };
      };
    };
  }> {
    return await request(`/superadmin/users/${id}`, { method: 'GET' });
  },
  async getSuperAdminMetrics(): Promise<{
    success: boolean;
    data: {
      users: {
        total: number;
        active: number;
        byTier: Record<string, number>;
        byTierActive?: Record<string, number>;
        byTierAll?: Record<string, number>;
      };
      sessions: {
        total: number;
        thisMonth: number;
        lastMonth: number;
        growth: number;
        liveVerifications: number;
      };
      apiKeys: { total: number; active: number; live: number };
      revenue: { mrr: number; currency: string };
    };
  }> {
    return await request('/superadmin/metrics', { method: 'GET' });
  },
  async updateSuperAdminUser(
    id: string,
    data: { securityTier?: string; status?: string },
  ): Promise<{
    success: boolean;
    data: { id: string; email: string; securityTier: string; status: string };
  }> {
    return await request(`/superadmin/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
  async requestPasswordReset(email: string): Promise<{ success: boolean; message?: string }> {
    return await request('/auth/reset-password/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },
  async confirmPasswordReset(
    email: string,
    token: string,
    newPassword: string,
  ): Promise<{ success: boolean; message?: string }> {
    return await request('/auth/reset-password/confirm', {
      method: 'POST',
      body: JSON.stringify({ email, token, newPassword }),
    });
  },
  async getStatus(): Promise<{ loggedIn: boolean; user?: AdminUser; csrfToken?: string }> {
    const raw = (await request('/auth/status', { method: 'GET' })) as any;
    if (!raw?.loggedIn) return { loggedIn: false };
    const user: AdminUser = {
      merchantId: raw.user?.id,
      email: raw.user?.email,
      role: 'admin',
      isSuperAdmin: raw.user?.isSuperAdmin || false,
    };
    if (raw?.csrfToken) setCsrfToken(raw.csrfToken);
    return { loggedIn: true, user, csrfToken: raw?.csrfToken };
  },
  // Demo Request APIs (Super Admin only)
  async listDemoRequests(params?: {
    status?: string;
    page?: number;
  }): Promise<{
    success: boolean;
    data: Array<{
      id: string;
      name: string;
      email: string;
      company: string | null;
      role: string | null;
      useCase: string | null;
      expectedVolume: string | null;
      message: string | null;
      status: string;
      ipAddress: string | null;
      createdAt: string;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  }> {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.page) qs.set('page', String(params.page));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return await request(`/superadmin/demo-requests${suffix}`, { method: 'GET' });
  },
  async updateDemoRequest(
    id: string,
    status: string,
  ): Promise<{
    success: boolean;
    data: {
      id: string;
      status: string;
    };
  }> {
    return await request(`/superadmin/demo-requests/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  },

  // ─── Playground ───
  async playgroundCreateSession(input: {
    environment: 'test' | 'live';
    checks: Array<{ type: string; value?: number | string }>;
    metadata?: Record<string, unknown>;
    successUrl?: string;
    cancelUrl?: string;
  }): Promise<{ success: boolean; data: any }> {
    return await request('/playground/sessions', {
      method: 'POST',
      body: JSON.stringify(input),
    }, {
      timeoutMs: PLAYGROUND_LONG_RUNNING_TIMEOUT_MS,
    });
  },

  async playgroundGetSession(id: string): Promise<{ success: boolean; data: any }> {
    return await request(`/playground/sessions/${id}`, { method: 'GET' });
  },

  async playgroundScenarios(): Promise<{
    success: boolean;
    data: Array<{
      id: string;
      name: string;
      description: string;
      category: string;
      tags?: string[];
      source?: 'built-in' | 'custom';
      results?: Record<string, boolean>;
      defaultResult?: boolean;
    }>;
  }> {
    return await request('/playground/scenarios', { method: 'GET' });
  },

  async playgroundCreateScenario(input: {
    name: string;
    description?: string;
    results: Record<string, boolean>;
    defaultResult?: boolean;
  }): Promise<{ success: boolean; data: { id: string; name: string } }> {
    return await request('/playground/scenarios', {
      method: 'POST',
      body: JSON.stringify(input),
    }, {
      timeoutMs: PLAYGROUND_LONG_RUNNING_TIMEOUT_MS,
    });
  },

  async playgroundDeleteScenario(id: string): Promise<{ success: boolean; message?: string }> {
    return await request(`/playground/scenarios/${id}`, { method: 'DELETE' });
  },

  async playgroundRuns(params?: {
    limit?: number;
    offset?: number;
    rangeDays?: number;
  }): Promise<{
    success: boolean;
    data: Array<{
      id: string;
      sessionId: string;
      outcome: string;
      scenarioId?: string | null;
      results: Record<string, boolean>;
      status: string;
      riskScore?: number;
      aiInsights?: string[];
      createdAt: string;
    }>;
    meta?: {
      total: number;
      limit: number;
      offset: number;
      rangeStart: string;
      rangeEnd: string;
      recentCount: number;
      proLimit: number;
      tier: string;
    };
  }> {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    if (params?.rangeDays) qs.set('rangeDays', String(params.rangeDays));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return await request(`/playground/runs${suffix}`, { method: 'GET' });
  },

  // ─── Blog Admin ───
  async blogAdminList(params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<{
    success: boolean;
    data: Array<{
      id: string;
      slug: string;
      title: string;
      excerpt: string | null;
      content: string;
      featuredImage: string | null;
      authorName: string;
      authorAvatar: string | null;
      status: string;
      publishedAt: string | null;
      tags: string[];
      createdAt: string;
      updatedAt: string;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  }> {
    const qs = new URLSearchParams();
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.status) qs.set('status', params.status);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return await request(`/blog/admin/posts${suffix}`, { method: 'GET' });
  },

  async blogAdminGet(id: string): Promise<{
    success: boolean;
    data: {
      id: string;
      slug: string;
      title: string;
      excerpt: string | null;
      content: string;
      featuredImage: string | null;
      authorName: string;
      authorAvatar: string | null;
      status: string;
      publishedAt: string | null;
      tags: string[];
      metadata: any;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    return await request(`/blog/admin/posts/${id}`, { method: 'GET' });
  },

  async blogAdminCreate(data: {
    title: string;
    slug?: string;
    excerpt?: string;
    content: string;
    featuredImage?: string;
    authorName: string;
    authorAvatar?: string;
    status?: 'draft' | 'published';
    tags?: string[];
  }): Promise<{ success: boolean; data: any }> {
    return await request('/blog/admin/posts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async blogAdminUpdate(
    id: string,
    data: {
      title?: string;
      slug?: string;
      excerpt?: string;
      content?: string;
      featuredImage?: string;
      authorName?: string;
      authorAvatar?: string;
      status?: 'draft' | 'published';
      tags?: string[];
    },
  ): Promise<{ success: boolean; data: any }> {
    return await request(`/blog/admin/posts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async blogAdminDelete(id: string): Promise<{ success: boolean; message?: string }> {
    return await request(`/blog/admin/posts/${id}`, { method: 'DELETE' });
  },

  async blogAdminUpload(file: File): Promise<{ success: boolean; url: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const headers: Record<string, string> = {};
    const token = getCsrfToken();
    if (token) headers['x-csrf-token'] = token;

    const res = await fetch(`${BASE_URL}/blog/admin/upload`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: formData,
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(data.error || 'Upload failed');
    }

    return await res.json();
  },

  async playgroundSimulate(
    id: string,
    outcome: 'pass_all' | 'fail_all' | 'custom' | 'scenario',
    customResults?: Record<string, boolean>,
    scenarioId?: string,
  ): Promise<{ success: boolean; data: any; message?: string }> {
    return await request(`/playground/simulate/${id}`, {
      method: 'POST',
      body: JSON.stringify({ outcome, customResults, scenarioId }),
    }, {
      timeoutMs: PLAYGROUND_LONG_RUNNING_TIMEOUT_MS,
    });
  },

  // Team Management
  async teamList(): Promise<{
    members: Array<{
      id: string;
      email: string;
      role: string;
      status: string;
      joinedAt: string | null;
      createdAt: string;
    }>;
    invites: Array<{
      id: string;
      email: string;
      role: string;
      status: string;
      expiresAt: string;
      createdAt: string;
    }>;
    limits: {
      maxTeamMembers: number;
      currentCount: number;
      hasTeamManagement: boolean;
    };
    ownerEmail: string;
    isAccountOwner: boolean;
    currentUserRole?: string;
  }> {
    return await request('/team', { method: 'GET' });
  },
  async teamInvite(email: string, role: string): Promise<{
    id: string;
    email: string;
    role: string;
    token: string;
    expiresAt: string;
  }> {
    return await request('/team/invite', {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    });
  },
  async teamRevokeInvite(inviteId: string): Promise<{ success: boolean }> {
    return await request(`/team/invite/${inviteId}`, { method: 'DELETE' });
  },
  async teamUpdateRole(memberId: string, role: string): Promise<{ success: boolean; role: string }> {
    return await request(`/team/${memberId}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    });
  },
  async teamRemoveMember(memberId: string): Promise<{ success: boolean }> {
    return await request(`/team/${memberId}`, { method: 'DELETE' });
  },
  async teamAcceptInvite(token: string): Promise<{
    success: boolean;
    needsRegistration: boolean;
    email: string;
    merchantId: string;
    sessionRefreshed?: boolean;
  }> {
    return await request('/team/accept', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  },

  async teamLeave(): Promise<{ success: boolean }> {
    return await request('/team/leave', { method: 'POST' });
  },
  async teamInviteInfo(token: string): Promise<{
    email: string;
    role: string;
    expiresAt: string;
    organization: string;
    invitedBy: { name: string; email: string } | null;
  }> {
    return await request(`/team/invite-info?token=${encodeURIComponent(token)}`, { method: 'GET' });
  },

  // Audit Log (customer-facing)
  async auditLog(params?: {
    action?: string;
    category?: string;
    severity?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }): Promise<{
    data: Array<{
      id: string;
      action: string;
      category: string;
      severity: string;
      apiKeyId: string | null;
      sessionId: string | null;
      ipAddress: string;
      userAgent: string | null;
      requestId: string | null;
      details: any;
      metadata: any;
      timestamp: string;
    }>;
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    const qs = new URLSearchParams();
    if (params?.action) qs.set('action', params.action);
    if (params?.category) qs.set('category', params.category);
    if (params?.severity) qs.set('severity', params.severity);
    if (params?.startDate) qs.set('startDate', params.startDate);
    if (params?.endDate) qs.set('endDate', params.endDate);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return await request(`/audit-log${suffix}`, { method: 'GET' });
  },

  // ─── Notifications ───

  async listNotifications(params?: {
    category?: string;
    isRead?: string;
    page?: number;
    limit?: number;
  }): Promise<{
    data: Array<{
      id: string;
      type: string;
      category: string;
      severity: string;
      title: string;
      message: string;
      actionUrl: string | null;
      isRead: boolean;
      metadata: any;
      createdAt: string;
    }>;
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    const qs = new URLSearchParams();
    if (params?.category) qs.set('category', params.category);
    if (params?.isRead) qs.set('isRead', params.isRead);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return await request(`/notifications${suffix}`, { method: 'GET' });
  },

  async notificationsUnreadCount(): Promise<{ count: number }> {
    return await request('/notifications/unread-count', { method: 'GET' });
  },

  async markNotificationRead(id: string): Promise<{ success: boolean }> {
    return await request(`/notifications/${id}/read`, { method: 'PATCH' });
  },

  async markAllNotificationsRead(): Promise<{ success: boolean }> {
    return await request('/notifications/mark-all-read', { method: 'POST' });
  },

  async deleteNotification(id: string): Promise<{ success: boolean }> {
    return await request(`/notifications/${id}`, { method: 'DELETE' });
  },

  async getNotificationPreferences(): Promise<{
    preferences: Record<string, { inApp: boolean; email: boolean }>;
  }> {
    return await request('/notifications/preferences', { method: 'GET' });
  },

  async updateNotificationPreferences(
    preferences: Record<string, { inApp: boolean; email: boolean }>,
  ): Promise<{ success: boolean }> {
    return await request('/notifications/preferences', {
      method: 'PUT',
      body: JSON.stringify({ preferences }),
    });
  },
};
